import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ListMusic, Compass, Heart, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import IPadWrapper from './IPadWrapper';

const Navbar: React.FC = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  // Todos os 5 botões da navbar em ordem
  const navItems = [
    { path: '/home', icon: Home, label: 'Início', isSpecial: false },
    { path: '/categorias', icon: ListMusic, label: 'Categorias', isSpecial: false },
    { path: '/caminhada', icon: Compass, label: 'Jornada', isSpecial: true }, // Botão especial no centro
    { path: '/favoritos', icon: Heart, label: 'Favoritos', isSpecial: false },
    { path: '/settings', icon: Settings, label: 'Config', isSpecial: false },
  ];

  const navbarContent = (
    <nav className="fixed bottom-0 left-0 right-0 w-full bg-background border-t border-[#FFD700] rounded-t-2xl z-40 safe-area-bottom navbar">
      {/* Container da navbar com altura responsiva */}
      <div className="px-3 py-1 md:px-6 md:py-1.5 lg:px-8 lg:py-2">
        {/* Grid com 5 colunas uniformes */}
        <div className="grid grid-cols-5 gap-1 md:gap-2 lg:gap-3">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "relative flex flex-col items-center justify-center py-1 px-1 md:py-1.5 md:px-2 lg:py-2 lg:px-3",
                "transition-all duration-200 ease-in-out touch-manipulation",
                "min-h-[48px] md:min-h-[56px] lg:min-h-[64px]",
                isActive(item.path) ? "text-amber-500" : "text-muted-foreground hover:text-amber-400"
              )}
            >
              {/* Container do ícone com tratamento especial para Jornada */}
              <div className={cn(
                "flex items-center justify-center mb-1 md:mb-2 transition-all duration-200",
                item.isSpecial ? [
                  "w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full",
                  "bg-gradient-to-r from-amber-400 to-yellow-200",
                  "border-2 border-amber-400",
                  isActive(item.path) ? "shadow-xl scale-110" : "shadow-lg"
                ] : "w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10"
              )}>
                <item.icon 
                  className={cn(
                    "transition-colors duration-200",
                    item.isSpecial ? [
                      "w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-[#23405a]",
                      isActive(item.path) ? "text-[#1a2f3f]" : "text-[#23405a]"
                    ] : "w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
                  )} 
                />
              </div>

              {/* Label do botão com tamanho responsivo */}
              <span className={cn(
                "text-xs md:text-sm lg:text-base leading-tight font-medium text-center whitespace-nowrap",
                "transition-colors duration-200 px-1",
                item.isSpecial ? [
                  isActive(item.path) ? "text-amber-600 font-semibold" : "text-amber-500 font-medium"
                ] : "",
                isActive(item.path) && !item.isSpecial ? "font-semibold" : "font-medium"
              )}>
                {item.label}
              </span>

              {/* Efeito de destaque sutil quando ativo (apenas para botões não especiais) */}
              {isActive(item.path) && !item.isSpecial && (
                <div className="absolute inset-0 bg-amber-500/10 rounded-lg opacity-50" />
              )}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );

  return (
    <IPadWrapper>
      {navbarContent}
    </IPadWrapper>
  );
};

export default Navbar; 